import React, { useContext, useEffect, useRef, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import Carousel from 'react-native-reanimated-carousel';
import FontAwesome6 from '@expo/vector-icons/FontAwesome6';
import PropTypes from 'prop-types';
import CdnImageBackground from '../common/CdnImageBackground';
import ComponentContainer from '../common/ComponentContainer';
import { H3, ScalableText } from '../text';
import LayoutContext from '../../contexts/Layout';

const PhotoGalleryItem = ({
    carouselRef,
    description,
    enableImageOverlay = false,
    followContentLink,
    image,
    index,
    isEnd,
    link,
    nodeTypeAlias = 'Photo',
    renderNavigation,
    setCarouselHeight,
    title,
    width
}) => {
    const [imageDimensions, setImageDimensions] = useState();

    useEffect(() => {
        if (imageDimensions?.height) {
            setCarouselHeight(() => imageDimensions.height);
        }
    }, [imageDimensions, setCarouselHeight]);

    return (
        <CdnImageBackground
            enableOverlay={enableImageOverlay}
            imageDimensions={imageDimensions}
            onPress={async () => await followContentLink(link.isExternal, link.internalKey, link.externalUrl)}
            setImageDimensions={setImageDimensions}
            uri={image.cdnImagePath}
            usePointer={!!link.internalKey || !!link.externalUrl}
            width={width}
            dataSet={{ umbracoComponentName: nodeTypeAlias }}
        >
            <ComponentContainer style={StyleSheet.flatten(Styles.contentContainer, { height: imageDimensions?.height })}>
                {renderNavigation && (
                    <View style={Styles.navigationControls}>
                        <FontAwesome6 color={index === 0 ? 'transparent' : '#fff'} name='chevron-left' onPress={() => carouselRef.current?.prev()} size={40} />
                        {!isEnd && <FontAwesome6 color='white' name='chevron-right' onPress={() => carouselRef.current?.next()} size={40} />}
                    </View>
                )}
                <View style={Styles.detailsContainer}>
                    <H3 style={{ color: 'white', paddingBottom: 2.5 }} text={title} />
                    <ScalableText style={Styles.description}>{description}</ScalableText>
                </View>
            </ComponentContainer>
        </CdnImageBackground>
    );
};

const PhotoGallery = ({ contentItems, followContentLink, nodeTypeAlias = 'PhotoGallery', width, hardWidth = width }) => {
    const { layout } = useContext(LayoutContext);
    const [activeIndex, setActiveIndex] = useState(0);
    const [carouselHeight, setCarouselHeight] = useState(600);
    const carouselRef = useRef();
    const items = Object.values(contentItems);

    return (
        <View
            style={{ marginBottom: layout.styleGuide.componentSeparator, height: carouselHeight, width: width }}
            dataSet={{ umbracoComponentName: nodeTypeAlias, spacerBottom: `StyleGuide.ComponentSeparator(${layout.styleGuide.componentSeparator})` }}
        >
            <Carousel
                autoPlay={false}
                data={items}
                loop={false}
                onConfigurePanGesture={panGesture => {
                    // fix panGesture so that the carousel works correctly
                    // within our parent ScrollView
                    panGesture.config.touchAction = 'pan-y'; // for web

                    if (!Platform.OS !== 'web') {
                        panGesture.activeOffsetX([-5, 5]);
                        panGesture.failOffsetY([-5, 5]);
                    }
                }}
                onScrollEnd={index => setActiveIndex(index)}
                ref={carouselRef}
                renderItem={({ item, index }) => {
                    const { key: itemKey, ...itemProps } = item;

                    return (
                        <PhotoGalleryItem
                            {...itemProps}
                            itemKey={itemKey}
                            carouselRef={carouselRef}
                            followContentLink={followContentLink}
                            index={index}
                            isEnd={index === items.length - 1}
                            setCarouselHeight={setCarouselHeight}
                            renderNavigation={items.length > 1 && !layout.device.isNative}
                            width={hardWidth}
                        />
                    );
                }}
                scrollAnimationDuration={500}
                snapEnabled={true}
                style={{
                    height: carouselHeight,
                    width: hardWidth
                }}
            />
            {items.length > 1 && (
                <View style={StyleSheet.flatten(Styles.paginationContainer, { marginVertical: layout.styleGuide.spacer3 })} testID='PhotoGallery.Pagination'>
                    {items.map((item, index) => {
                        return (
                            <View
                                key={item.id}
                                style={StyleSheet.flatten(Styles.paginationDot, {
                                    backgroundColor: index === activeIndex ? '#ffffff' : '#dde4e9',
                                    borderColor: index === activeIndex ? '#153d6e' : null,
                                    borderWidth: index === activeIndex ? 1 : 0
                                })}
                            />
                        );
                    })}
                </View>
            )}
        </View>
    );
};

const Styles = StyleSheet.create({
    contentContainer: {
        backgroundColor: 'transparent'
    },
    description: {
        color: 'white',
        fontFamily: 'GothamBook'
    },
    detailsContainer: {
        bottom: 20,
        color: 'white',
        position: 'absolute'
    },
    navigationControls: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        top: '40%'
    },
    paginationContainer: {
        alignItems: 'center',
        backgroundColor: 'white',
        flexDirection: 'row',
        height: 12,
        justifyContent: 'center'
    },
    paginationDot: {
        borderRadius: 25,
        height: 10,
        marginHorizontal: 2,
        width: 10
    }
});

PhotoGallery.propTypes = {
    items: PropTypes.array,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

export default PhotoGallery;
